import Phaser from "phaser";

import {
  TrophyHallScene,
  type TrophyCelebration,
  type TrophyHallVisualState,
} from "./TrophyHallScene";

export type TrophyHallRuntime = {
  game: Phaser.Game;
  scene: TrophyHallScene;
  ready: Promise<void>;
  renderScale: number;
  resize(width: number, height: number): void;
  playCelebration(kind: TrophyCelebration): void;
  destroy(): void;
};

const MAX_RENDER_SCALE = 2;

function getRenderScale(): number {
  return Math.min(MAX_RENDER_SCALE, Math.max(1, window.devicePixelRatio || 1));
}

export function createTrophyHallGame(
  parent: HTMLElement,
  visualState: TrophyHallVisualState,
): TrophyHallRuntime {
  const renderScale = getRenderScale();
  const logicalWidth = parent.clientWidth || 360;
  const logicalHeight = parent.clientHeight || 332;
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const scene = new TrophyHallScene({ onReady: resolveReady }, visualState, renderScale);
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent,
    width: Math.round(logicalWidth * renderScale),
    height: Math.round(logicalHeight * renderScale),
    transparent: true,
    antialias: true,
    render: {
      antialias: true,
      pixelArt: false,
      roundPixels: false,
    },
    scale: {
      mode: Phaser.Scale.NONE,
      autoCenter: Phaser.Scale.NO_CENTER,
    },
    scene: [scene],
    banner: false,
  });

  const canvas = parent.querySelector("canvas");
  if (canvas !== null) {
    canvas.style.pointerEvents = "none";
    canvas.setAttribute("aria-hidden", "true");
  }

  return {
    game,
    scene,
    ready,
    renderScale,
    resize(width: number, height: number): void {
      game.scale.resize(Math.round(width * renderScale), Math.round(height * renderScale));
    },
    playCelebration(kind: TrophyCelebration): void {
      scene.playCelebration(kind);
    },
    destroy(): void {
      game.destroy(true);
    },
  };
}
